import {
  Page,
  Text,
  View,
  StyleSheet,
  Link,
  Image,
  Font,
} from "@react-pdf/renderer";

import { sortItemsByTermAsc, sortTermsAsc } from "../../utils/generalUtils";
import upouLogo from "../components/upou-university-logo.png";

import ArialRegular from "../components/Arial/ARIAL.TTF";
import ArialBold from "../components/Arial/ARIALBD.TTF";
import PalatinoBold from "../components/Palatino/PalatinoBold.ttf";

Font.registerHyphenationCallback((word) => [word]);
Font.register({ family: "Arial", src: ArialRegular });
Font.register({ family: "ArialBold", src: ArialBold });
Font.register({ family: "PalatinoBold", src: PalatinoBold });

const styles = StyleSheet.create({
  page: {
    paddingTop: 90,
    paddingHorizontal: 40,
    paddingBottom: 40,
    fontSize: 11,
    fontFamily: "Arial",
  },

  header: {
    position: "absolute",
    top: 20,
    left: 40,
    right: 40,
    flexDirection: "row",
    alignItems: "center",
    borderBottom: "1px solid #000",
    paddingBottom: 5,
  },

  logo: {
    width: 40,
    height: 40,
    marginRight: 10,
  },

  university: {
    fontFamily: "ArialBold",
    color: "#862633",
  },

  subtitle: {
    paddingTop: 2.5,
  },

  title: {
    paddingTop: 150,
    fontFamily: "PalatinoBold",
    letterSpacing: -1,
    fontSize: 50,
    textAlign: "center",
    color: "#862633",
  },

  name: {
    paddingTop: 20,
    paddingBottom: 50,
    fontSize: 15,
    textAlign: "center",
  },

  section: {
    paddingTop: 50,
    textAlign: "center",
    lineHeight: 1,
  },

  label: {
    fontFamily: "ArialBold",
    color: "#862633",
  },

  sectionTitle: {
    paddingTop: 10,
    fontSize: 18,
    fontFamily: "PalatinoBold",
    letterSpacing: -1,
    textAlign: "center",
    marginBottom: 20,
    color: "#862633",
  },

  table: {
    display: "table",
    width: "auto",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },

  row: {
    flexDirection: "row",
  },

  cellLabel: {
    width: "45%",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    backgroundColor: "#ededed",
  },

  cellValue: {
    width: "55%",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
  },

  itemContainer: {
    marginBottom: 20,
  },

  link: {
    color: "#1777ff",
  },
});

export default function PortfolioFormat({ contents }) {
  const sortedSelectedTerms = sortTermsAsc([...(contents.selectedTerms || [])]);

  return (
    <>
      {/* COVER PAGE */}
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src={upouLogo} style={styles.logo} />

          <View>
            <Text style={styles.university}>
              University of the Philippines Open University
            </Text>
            <Text style={styles.subtitle}>Faculty Portfolio</Text>
          </View>
        </View>

        <Text style={styles.title}>Faculty Portfolio</Text>

        <Text style={styles.name}>{contents.facultyName}</Text>

        <View style={styles.section}>
          <Text style={styles.label}>Sections</Text>

          {contents.sections.map((section, i) => (
            <Text key={i}>{section.title}</Text>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Academic Terms</Text>

          {sortedSelectedTerms.length === 0 ? (
            <Text>All</Text>
          ) : (
            sortedSelectedTerms.map((term, i) => <Text key={i}>{term}</Text>)
          )}
        </View>
      </Page>

      {/* SECTION PAGES */}
      {contents.sections.map((section, sectionIndex) => {
        const items = sortItemsByTermAsc(section.items || []);

        return (
          <Page key={sectionIndex} size="A4" style={styles.page} wrap>
            <View style={styles.header} fixed>
              <Image src={upouLogo} style={styles.logo} />
              <View>
                <Text style={styles.university}>
                  University of the Philippines Open University
                </Text>
                <Text style={styles.subtitle}>
                  Faculty Portfolio | {section.title}
                </Text>
              </View>
            </View>

            <Text style={styles.sectionTitle}>{section.title}</Text>

            {items.length === 0 ? (
              <Text>No records found.</Text>
            ) : (
              items.map((item, index) => (
                <View key={index} style={styles.itemContainer} wrap={false}>
                  <Text
                    style={{
                      marginBottom: 5,
                      fontFamily: "ArialBold",
                    }}
                  >
                    {index + 1}.
                  </Text>

                  <View style={styles.table}>
                    {section.fields.map((field) => {
                      let value = item[field.name];

                      if (
                        value === null ||
                        value === undefined ||
                        value === ""
                      ) {
                        return null;
                      }

                      if (field.type === "yes-no-radio") {
                        if (value === true || value === 1) value = "Yes";
                        else if (value === false || value === 0) value = "No";
                      }

                      const isLink =
                        typeof value === "string" && value.startsWith("http");

                      return (
                        <View style={styles.row} key={field.name}>
                          <Text style={styles.cellLabel}>{field.label}</Text>

                          <Text style={styles.cellValue}>
                            {isLink ? (
                              <Link src={value} style={styles.link}>
                                {value}
                              </Link>
                            ) : (
                              String(value)
                            )}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              ))
            )}
          </Page>
        );
      })}
    </>
  );
}
